/**
 * Platform Admin Layout Component
 * 
 * Provides the main layout structure for the admin portal with sidebar navigation
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { authState, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navigationItems = [
    {
      path: '/dashboard',
      icon: 'bi-speedometer2',
      label: 'Dashboard',
      description: 'System overview'
    },
    {
      path: '/companies',
      icon: 'bi-building',
      label: 'Companies',
      description: 'Manage companies'
    },
    {
      path: '/users',
      icon: 'bi-people',
      label: 'Users',
      description: 'Platform users'
    },
    {
      path: '/analytics',
      icon: 'bi-graph-up',
      label: 'Analytics',
      description: 'System metrics'
    },
    {
      path: '/security',
      icon: 'bi-shield-check',
      label: 'Security',
      description: 'Security monitoring'
    },
    {
      path: '/audit-logs',
      icon: 'bi-journal-text',
      label: 'Audit Logs',
      description: 'System audit trail'
    },
    {
      path: '/permissions',
      icon: 'bi-key',
      label: 'Permissions',
      description: 'Permission management'
    },
    {
      path: '/settings',
      icon: 'bi-gear',
      label: 'Settings',
      description: 'System settings'
    },
    {
      path: '/health',
      icon: 'bi-heart-pulse',
      label: 'System Health',
      description: 'Health monitoring'
    }
  ];

  return (
    <div className="d-flex">
      {/* Sidebar */}
      <div className={`admin-sidebar ${showMobileSidebar ? 'show' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`} 
           style={{ width: sidebarCollapsed ? '80px' : '280px', transition: 'width 0.3s ease' }}>
        
        {/* Sidebar Header */}
        <div className="p-4 border-bottom border-light border-opacity-25">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <i className="bi bi-shield-lock text-white" style={{ fontSize: '2rem' }}></i>
            </div>
            {!sidebarCollapsed && (
              <div>
                <h5 className="text-white mb-0 fw-bold">Platform Admin</h5>
                <small className="text-white-50">System Control</small>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-grow-1 py-3">
          <ul className="nav flex-column">
            {navigationItems.map((item) => (
              <li key={item.path} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link d-flex align-items-center ${isActiveRoute(item.path) ? 'active' : ''}`}
                  onClick={() => setShowMobileSidebar(false)}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <i className={`bi ${item.icon}`}></i>
                  {!sidebarCollapsed && (
                    <div className="ms-3">
                      <div className="fw-medium">{item.label}</div>
                      <small className="opacity-75">{item.description}</small>
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-3 border-top border-light border-opacity-25">
          <Link
            to="/profile"
            className={`nav-link d-flex align-items-center ${isActiveRoute('/profile') ? 'active' : ''}`}
            title={sidebarCollapsed ? 'Profile' : ''}
          >
            <i className="bi bi-person-circle"></i>
            {!sidebarCollapsed && (
              <div className="ms-3">
                <div className="fw-medium">Profile</div>
                <small className="opacity-75">{authState.user?.email}</small>
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showMobileSidebar && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-md-none"
          style={{ zIndex: 1040 }}
          onClick={() => setShowMobileSidebar(false)}
        ></div>
      )}

      {/* Main Content Area */}
      <div className="flex-grow-1">
        {/* Header */}
        <header className="admin-header d-flex align-items-center justify-content-between px-4 py-3">
          <div className="d-flex align-items-center">
            {/* Sidebar Toggle */}
            <button
              onClick={toggleSidebar}
              className="btn btn-link text-muted p-0 me-3 d-none d-md-block"
              style={{ fontSize: '1.25rem' }}
            >
              <i className={`bi ${sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={toggleMobileSidebar}
              className="btn btn-link text-muted p-0 me-3 d-md-none"
              style={{ fontSize: '1.25rem' }}
            >
              <i className="bi bi-list"></i>
            </button>

            {/* Breadcrumb */}
            <nav aria-label="breadcrumb">
              <ol className="admin-breadcrumb breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard" className="text-decoration-none">
                    <i className="bi bi-house-door me-1"></i>
                    Dashboard
                  </Link>
                </li>
                {location.pathname !== '/dashboard' && location.pathname !== '/' && (
                  <li className="breadcrumb-item active">
                    {navigationItems.find(item => isActiveRoute(item.path))?.label || 'Page'}
                  </li>
                )}
              </ol>
            </nav>
          </div>

          {/* Header Actions */}
          <div className="d-flex align-items-center">
            {/* System Status Indicator */}
            <div className="me-3 d-none d-lg-block">
              <span className="badge bg-success d-flex align-items-center">
                <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem' }}></i>
                System Online
              </span>
            </div>

            {/* Notifications */}
            <div className="dropdown me-3">
              <button
                className="btn btn-link text-muted p-0 position-relative"
                type="button"
                data-bs-toggle="dropdown"
                style={{ fontSize: '1.25rem' }}
              >
                <i className="bi bi-bell"></i>
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                  3
                </span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><h6 className="dropdown-header">System Notifications</h6></li>
                <li><a className="dropdown-item" href="#"><i className="bi bi-exclamation-triangle text-warning me-2"></i>High CPU usage detected</a></li>
                <li><a className="dropdown-item" href="#"><i className="bi bi-info-circle text-info me-2"></i>New company registered</a></li>
                <li><a className="dropdown-item" href="#"><i className="bi bi-shield-check text-success me-2"></i>Security scan completed</a></li>
                <li><hr className="dropdown-divider" /></li>
                <li><a className="dropdown-item text-center" href="#">View all notifications</a></li>
              </ul>
            </div>

            {/* User Menu */}
            <div className="dropdown">
              <button
                className="btn btn-link text-muted p-0 d-flex align-items-center"
                type="button"
                data-bs-toggle="dropdown"
              >
                <div className="me-2 text-end d-none d-sm-block">
                  <div className="fw-medium text-dark" style={{ fontSize: '0.875rem' }}>
                    {authState.user?.firstName} {authState.user?.lastName}
                  </div>
                  <small className="text-muted">Platform Admin</small>
                </div>
                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                     style={{ width: '40px', height: '40px' }}>
                  {authState.user?.firstName?.[0]}{authState.user?.lastName?.[0]}
                </div>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><h6 className="dropdown-header">Admin Account</h6></li>
                <li>
                  <Link className="dropdown-item" to="/profile">
                    <i className="bi bi-person me-2"></i>
                    Profile Settings
                  </Link>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Sign Out
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
}